import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAt,
  IconCake,
  IconDotsVertical,
  IconEye,
  IconMail,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUserCheck,
  IconUserOff,
  IconUserPlus,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useCustomerPortalPreview } from '../../hooks/useCustomerPortalPreview';
import { CallButton } from '../../components/call/CallButton';
import type { HubCustomerContext } from '../../lib/api/domains/technicianHub';
import {
  DIVERGENCE_FIELD_LABELS,
  RESPONSIBILITY_LABELS,
  RESPONSIBILITY_ORDER,
  type CareContact,
  type CrmDivergence,
} from '../../lib/api/domains/customerCare';
import { CareContactFormModal } from './CareContactFormModal';
import { InfoCard, InfoEmpty } from './CustomerInfoParts';
import { useCustomerCare, type CustomerInfoIdent } from './customerInfoQueries';
import classes from './CustomerInfo.module.css';

/**
 * Kontakte, die das CRM seit über 30 Tagen nicht mehr meldet — Hinweis auf einen
 * Abgang. Bewusst NUR ein Hinweis: automatisch deaktivieren würde bei einem
 * CRM-Ausfall reihenweise aktive Kontakte stilllegen.
 */
const CRM_STALE_DAYS = 30;
function isStaleInCrm(lastSeen: string | null | undefined): boolean {
  if (!lastSeen) return false; // Nie gesehen = rein lokaler Kontakt, kein Abgang.
  const age = Date.now() - new Date(lastSeen).getTime();
  return age > CRM_STALE_DAYS * 24 * 60 * 60 * 1000;
}

/** "1990-05-12" → "12.05.1990" (reine String-Formatierung, keine Zeitzonen-Tücken). */
function formatBirthday(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Aktive zuerst, darin der Haupt-Ansprechpartner vorn, dann alphabetisch. */
function compareContacts(a: CareContact, b: CareContact): number {
  if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
  if (!!a.isPrimary !== !!b.isPrimary) return a.isPrimary ? -1 : 1;
  return a.name.localeCompare(b.name, 'de');
}

/** Suche über Name, Mail, Position, Hinweise — und Rufnummern ohne Rücksicht auf die Schreibweise. */
function matchesContact(c: CareContact, query: string): boolean {
  if (!query) return true;
  if ([c.name, c.email, c.jobRole, c.notes].some((v) => v?.toLowerCase().includes(query))) return true;
  const digits = query.replace(/\D/g, '');
  return digits.length >= 3 && c.phones.some((p) => p.number.replace(/\D/g, '').includes(digits));
}

const DIMMED_ICON = { color: 'var(--mantine-color-dimmed)', flexShrink: 0 } as const;

/**
 * Mail-Domains — neben den Ansprechpartnern, weil beides beantwortet, wer zu diesem
 * Kunden gehört. Lesen alle, ändern nur Admins: die Domains entscheiden über die
 * Zuordnung von Portal-Anfragen und Ticket-Absendern (auch durch KIAN).
 */
function EmailDomainsCard({ customerId }: { customerId: string }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const domainsKey = ['customer-email-domains', customerId] as const;
  const { data } = useQuery({
    queryKey: domainsKey,
    queryFn: () => api.getCustomerEmailDomains(customerId),
    staleTime: 60_000,
  });
  const [newDomain, setNewDomain] = useState('');
  const mutation = useMutation({
    mutationFn: (action: { kind: 'add' | 'remove'; domain: string }) =>
      action.kind === 'add'
        ? api.addCustomerEmailDomain(customerId, action.domain)
        : api.removeCustomerEmailDomain(customerId, action.domain),
    onSuccess: (result, action) => {
      queryClient.setQueryData(domainsKey, result);
      if (action.kind === 'add') setNewDomain('');
    },
    onError: (e: Error) => notifications.show({ title: 'Domain nicht gespeichert', message: e.message, color: 'red' }),
  });

  const domains = data?.domains ?? [];
  const add = () => {
    if (newDomain.trim()) mutation.mutate({ kind: 'add', domain: newDomain });
  };

  return (
    <InfoCard
      icon={<IconAt size={15} />}
      color="cyan"
      title="Mail-Domains"
      count={domains.length}
      hint={
        'Portal-Anmeldungen und Ticket-Absender dieser Domains werden diesem Kunden zugeordnet. ' +
        'Dieselbe Domain darf nur bei Firmen derselben Firmengruppe stehen.' +
        (isAdmin ? '' : ' Ändern können nur Admins.')
      }
    >
      <Group gap={6} wrap="wrap">
        {domains.length === 0 && (
          <Text size="xs" c="dimmed" style={isAdmin ? { flexBasis: '100%' } : undefined}>
            Keine hinterlegt — zugeordnet wird nur über die E-Mail-Adressen der Ansprechpartner.
          </Text>
        )}
        {domains.map((d) => (
          <Badge
            key={d}
            size="lg"
            radius="sm"
            variant="light"
            color="gray"
            style={{ textTransform: 'none', fontWeight: 500 }}
            leftSection={<IconAt size={12} />}
            rightSection={
              isAdmin ? (
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="gray"
                  aria-label={`${d} entfernen`}
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ kind: 'remove', domain: d })}
                >
                  <IconX size={12} />
                </ActionIcon>
              ) : undefined
            }
          >
            {d}
          </Badge>
        ))}
        {isAdmin && (
          <Group gap={4} wrap="nowrap">
            <TextInput
              size="xs"
              w={190}
              placeholder="z. B. kunde.de"
              aria-label="Neue Mail-Domain"
              value={newDomain}
              onChange={(e) => setNewDomain(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconPlus size={12} />}
              disabled={!newDomain.trim()}
              loading={mutation.isPending && mutation.variables?.kind === 'add'}
              onClick={add}
            >
              Hinterlegen
            </Button>
          </Group>
        )}
      </Group>
    </InfoCard>
  );
}

function ContactRow({
  contact: c,
  customerId,
  onEdit,
  onPreview,
  previewPending,
  onToggleActive,
  togglePending,
  onDelete,
  onResolveDivergence,
  divergencePending,
}: {
  contact: CareContact;
  customerId: string;
  onEdit: () => void;
  onPreview: () => void;
  previewPending: boolean;
  onToggleActive: () => void;
  togglePending: boolean;
  onDelete: () => void;
  onResolveDivergence: (action: 'adopt' | 'dismiss') => void;
  divergencePending: boolean;
}) {
  const stale = isStaleInCrm(c.lastSeenInCrmAt);
  // Themen-Zuständigkeiten (Angebote, Rechnungen, …) — PRIMARY hat ein eigenes Abzeichen.
  const topics = RESPONSIBILITY_ORDER.filter((r) => r !== 'PRIMARY' && (c.responsibilities ?? []).includes(r));
  const divergence = Object.keys(c.crmDivergence ?? {}) as (keyof CrmDivergence)[];
  const hasBadges = c.isPrimary || topics.length > 0 || c.portalEnabled || stale || !c.isActive;

  return (
    <div className={c.isActive ? classes.row : `${classes.row} ${classes.muted}`}>
      <Group wrap="nowrap" align="flex-start" gap="sm">
        <Avatar name={c.name} color="initials" radius="xl" size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={8} wrap="wrap" align="baseline">
            <Text size="sm" fw={600}>
              {c.name}
            </Text>
            {c.jobRole && (
              <Text size="xs" c="dimmed">
                {c.jobRole}
              </Text>
            )}
          </Group>

          {hasBadges && (
            <Group gap={4} mt={4} wrap="wrap">
              {c.isPrimary && (
                <Tooltip label="Haupt-Ansprechpartner — wird bei Rückfragen ohne klaren Melder zum Anrufen angeboten">
                  <Badge size="xs" variant="filled" color="cyberOrange">
                    Haupt-AP
                  </Badge>
                </Tooltip>
              )}
              {topics.map((r) => (
                <Badge key={r} size="xs" variant="light" color="grape">
                  {RESPONSIBILITY_LABELS[r]}
                </Badge>
              ))}
              {c.portalEnabled && (
                <Badge size="xs" variant="light" color="teal">
                  Portal
                </Badge>
              )}
              {stale && (
                <Tooltip
                  label="Taucht seit über 30 Tagen nicht mehr im CRM auf — vermutlich ausgeschieden. Der Portal-Zugang wird beim Verschwinden aus dem CRM automatisch entzogen; der Datensatz bleibt erhalten."
                  multiline
                  w={280}
                >
                  <Badge size="xs" variant="light" color="yellow">
                    nicht mehr im CRM
                  </Badge>
                </Tooltip>
              )}
              {stale && !c.portalEnabled && (
                <Tooltip
                  label="Portal-Zugang entzogen — der Kontakt ist im CRM nicht mehr gelistet. Im Admin wieder freischaltbar."
                  multiline
                  w={260}
                >
                  <Badge size="xs" variant="light" color="red">
                    Zugang gesperrt
                  </Badge>
                </Tooltip>
              )}
              {!c.isActive && (
                <Badge size="xs" variant="light" color="gray">
                  inaktiv
                </Badge>
              )}
            </Group>
          )}

          <Group gap="md" mt={6} wrap="wrap">
            {c.email && (
              <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                <IconMail size={13} style={DIMMED_ICON} />
                <Anchor href={`mailto:${c.email}`} size="xs" c="dimmed" underline="hover" style={{ overflowWrap: 'anywhere' }}>
                  {c.email}
                </Anchor>
              </Group>
            )}
            {c.phones.map((p, i) => (
              <Group key={`${p.number}-${i}`} gap={2} wrap="nowrap">
                <CallButton
                  phone={p.number}
                  contactName={c.name}
                  customerId={customerId}
                  contactId={c.id}
                  originType="contact"
                  originId={c.id}
                  size="xs"
                />
                <Text size="xs" c="dimmed">
                  {p.number}
                  {p.label ? ` · ${p.label}` : ''}
                </Text>
              </Group>
            ))}
            {c.birthday && (
              <Group gap={4} wrap="nowrap">
                <IconCake size={13} style={DIMMED_ICON} />
                <Text size="xs" c="dimmed">
                  {formatBirthday(c.birthday)}
                </Text>
              </Group>
            )}
            {!c.email && c.phones.length === 0 && (
              <Text size="xs" c="dimmed">
                Keine Kontaktdaten
              </Text>
            )}
          </Group>

          {c.notes && (
            <Text size="xs" c="dimmed" fs="italic" lineClamp={2} mt={6}>
              {c.notes}
            </Text>
          )}

          {/*
            CRM-Abweichung: der Sync hat den gepflegten Wert bewusst stehen lassen und
            meldet nur, dass Weclapp etwas anderes sagt. Ohne diese Anzeige arbeitet man
            sonst unbemerkt mit einer toten Adresse.
          */}
          {divergence.length > 0 && (
            <Alert color="yellow" variant="light" radius="sm" p="xs" mt={8}>
              <Text size="xs" fw={600} mb={2}>
                Weclapp meldet abweichende Daten:
              </Text>
              <Stack gap={0} mb={6}>
                {divergence.map((f) => (
                  <Text size="xs" key={f}>
                    {DIVERGENCE_FIELD_LABELS[f]}: <b>{c.crmDivergence?.[f]?.crm}</b>
                  </Text>
                ))}
              </Stack>
              <Group gap={4}>
                <Button size="compact-xs" color="yellow" loading={divergencePending} onClick={() => onResolveDivergence('adopt')}>
                  Übernehmen
                </Button>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => onResolveDivergence('dismiss')}>
                  Unsere Daten behalten
                </Button>
              </Group>
            </Alert>
          )}
        </div>

        <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
          {c.isActive && (
            <Tooltip
              label={
                c.portalEnabled
                  ? `Portal ansehen — so, wie ${c.name} es sieht (nur lesen)`
                  : `Portal ansehen — so, wie ${c.name} es nach der Freischaltung sähe (nur lesen)`
              }
              multiline
              w={240}
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                color="orange"
                aria-label={`Kundenportal als ${c.name} ansehen`}
                loading={previewPending}
                onClick={onPreview}
              >
                <IconEye size={15} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Bearbeiten">
            <ActionIcon variant="subtle" size="sm" color="gray" aria-label={`${c.name} bearbeiten`} onClick={onEdit}>
              <IconPencil size={15} />
            </ActionIcon>
          </Tooltip>
          {/* Deaktivieren und Löschen liegen im Menü: in jeder Zeile als roter Knopf wären sie die lauteste — und riskanteste — Stelle. */}
          <Menu position="bottom-end" shadow="md" zIndex={1100} withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                aria-label={`Weitere Aktionen für ${c.name}`}
                loading={togglePending}
              >
                <IconDotsVertical size={15} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {c.isActive ? (
                <Menu.Item color="red" leftSection={<IconUserOff size={14} />} onClick={onToggleActive}>
                  Deaktivieren
                </Menu.Item>
              ) : (
                <Menu.Item leftSection={<IconUserCheck size={14} />} onClick={onToggleActive}>
                  Wieder aktivieren
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
                Löschen
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </div>
  );
}

/**
 * Reiter „Ansprechpartner" der Kundeninfos: Mail-Domains und die lokal gepflegten
 * Ansprechpartner mit Position, Mail, mehreren Rufnummern, Geburtstag, Zuständigkeiten
 * und Hinweisen. Portal-Zugang wird hier bewusst NICHT vergeben — das bleibt im Admin.
 */
export function CustomerContactsPanel({
  ident,
  fallbackContacts,
}: {
  ident: CustomerInfoIdent;
  /** Nur-Lese-Liste aus dem CRM, falls der Kunde lokal (noch) nicht existiert. */
  fallbackContacts: HubCustomerContext['contacts'];
}) {
  const queryClient = useQueryClient();
  const portalPreview = useCustomerPortalPreview();
  const { data, isLoading, queryKey } = useCustomerCare(ident);
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['hub-customer-context'] });
  };

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  /** Kontakt im Formular — null = neu anlegen. Bleibt beim Schließen stehen, damit der Titel nicht umspringt. */
  const [formContact, setFormContact] = useState<CareContact | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const contacts = data?.contacts;
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    // Wer sucht, findet auch Inaktive — sonst sähe „niemand gefunden" wie ein fehlender Kontakt aus.
    return (contacts ?? [])
      .filter((c) => (showInactive || c.isActive || query.length > 0) && matchesContact(c, query))
      .sort(compareContacts);
  }, [contacts, search, showInactive]);

  const openAdd = () => {
    setFormContact(null);
    setFormOpen(true);
  };
  const openEdit = (c: CareContact) => {
    setFormContact(c);
    setFormOpen(true);
  };

  /**
   * CRM-Abweichung entscheiden. Der Sync überschreibt gepflegte Werte nicht mehr,
   * deshalb liegt die Entscheidung hier — übernehmen oder Vermerk verwerfen.
   */
  const divergenceMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'adopt' | 'dismiss' }) =>
      api.resolveCareContactDivergence(id, action),
    onSuccess: (_res, vars) => {
      invalidate();
      notifications.show({
        title: vars.action === 'adopt' ? 'CRM-Werte übernommen' : 'Hinweis verworfen',
        message: '',
        color: 'green',
      });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (c: CareContact) => api.updateCareContact(c.id, { isActive: !c.isActive }),
    onSuccess: (_res, c) => {
      invalidate();
      notifications.show({
        title: c.isActive ? 'Ansprechpartner deaktiviert' : 'Ansprechpartner wieder aktiv',
        message: c.name,
        color: 'green',
      });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  /** Kontakt im Lösch-Dialog — null = Dialog zu. */
  const [deleteContact, setDeleteContact] = useState<CareContact | null>(null);

  /**
   * Löschen setzt serverseitig einen Löschvermerk. Die Meldung bietet „Rückgängig" an:
   * Das bringt den Kontakt mit seinem alten Aktiv-Stand zurück — den Portal-Zugang nicht.
   */
  const deleteMutation = useMutation({
    mutationFn: (c: CareContact) => api.deleteCareContact(c.id),
    onSuccess: (_res, c) => {
      setDeleteContact(null);
      invalidate();
      const notificationId = `contact-deleted-${c.id}`;
      notifications.show({
        id: notificationId,
        title: 'Ansprechpartner gelöscht',
        message: (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm">{c.name}</Text>
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                notifications.hide(notificationId);
                api
                  .restoreCareContact(c.id, c.isActive)
                  .then(() => {
                    invalidate();
                    notifications.show({
                      title: 'Ansprechpartner wiederhergestellt',
                      message: c.portalEnabled ? `${c.name} — den Portal-Zugang bitte im Admin neu freischalten.` : c.name,
                      color: 'green',
                    });
                  })
                  .catch((e: Error) =>
                    notifications.show({ title: 'Nicht wiederhergestellt', message: e.message, color: 'red' }),
                  );
              }}
            >
              Rückgängig
            </Button>
          </Group>
        ),
        color: 'green',
        autoClose: 8000,
      });
    },
    onError: (e: Error) => notifications.show({ title: 'Nicht gelöscht', message: e.message, color: 'red' }),
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  // Kein lokaler Kunde (z. B. noch nie gesynct) → Nur-Lese-Liste aus dem CRM.
  if (!data) {
    return (
      <Stack gap="md">
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          Dieser Kunde ist im ServiceHub noch nicht angelegt. Die Liste kommt direkt aus dem CRM und lässt sich
          erst nach der Synchronisierung pflegen.
        </Alert>
        <InfoCard icon={<IconUsers size={15} />} color="grape" title="Ansprechpartner" count={fallbackContacts.length} flush>
          {fallbackContacts.length === 0 ? (
            <InfoEmpty icon={<IconUsers size={20} />}>Keine Ansprechpartner</InfoEmpty>
          ) : (
            fallbackContacts.map((c) => (
              <Group key={c.id} className={classes.row} gap="sm" wrap="nowrap">
                <Avatar name={c.name} color="initials" radius="xl" size={32} />
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {c.name}
                  </Text>
                  {c.email && (
                    <Text size="xs" c="dimmed">
                      {c.email}
                    </Text>
                  )}
                </div>
              </Group>
            ))
          )}
        </InfoCard>
      </Stack>
    );
  }

  const all = data.contacts;
  const inactiveCount = all.filter((c) => !c.isActive).length;
  const activeCount = all.length - inactiveCount;
  const searchable = all.length > 5;

  return (
    <Stack gap="md">
      <EmailDomainsCard customerId={data.customer.id} />

      <InfoCard
        icon={<IconUsers size={15} />}
        color="grape"
        title="Ansprechpartner"
        count={activeCount}
        actions={
          <Button size="compact-sm" leftSection={<IconUserPlus size={14} />} onClick={openAdd}>
            Hinzufügen
          </Button>
        }
        flush
      >
        {(searchable || inactiveCount > 0) && (
          <Group className={classes.row} justify="space-between" gap="sm" wrap="wrap">
            {searchable ? (
              <TextInput
                size="xs"
                leftSection={<IconSearch size={14} />}
                placeholder="Name, E-Mail, Telefon oder Position"
                aria-label="Ansprechpartner suchen"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ flex: '1 1 220px', maxWidth: 360 }}
              />
            ) : (
              <span />
            )}
            {inactiveCount > 0 && (
              <Switch
                size="xs"
                label={`Inaktive anzeigen (${inactiveCount})`}
                checked={showInactive}
                onChange={(e) => setShowInactive(e.currentTarget.checked)}
              />
            )}
          </Group>
        )}

        {visible.map((c) => (
          <ContactRow
            key={c.id}
            contact={c}
            customerId={data.customer.id}
            onEdit={() => openEdit(c)}
            onPreview={() => portalPreview.open(c)}
            previewPending={portalPreview.pendingContactId === c.id}
            onToggleActive={() => toggleActiveMutation.mutate(c)}
            togglePending={toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === c.id}
            onDelete={() => setDeleteContact(c)}
            onResolveDivergence={(action) => divergenceMutation.mutate({ id: c.id, action })}
            divergencePending={divergenceMutation.isPending && divergenceMutation.variables?.id === c.id}
          />
        ))}

        {all.length === 0 ? (
          <InfoEmpty
            icon={<IconUsers size={20} />}
            action={
              <Button size="xs" variant="light" leftSection={<IconUserPlus size={14} />} onClick={openAdd}>
                Ersten Ansprechpartner anlegen
              </Button>
            }
          >
            Noch keine Ansprechpartner hinterlegt.
          </InfoEmpty>
        ) : visible.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            {search.trim() ? 'Niemand passt zur Suche.' : 'Alle Ansprechpartner sind inaktiv.'}
          </Text>
        ) : null}
      </InfoCard>

      <CareContactFormModal
        opened={formOpen}
        customerId={data.customer.id}
        contact={formContact}
        onClose={() => setFormOpen(false)}
      />

      <Modal
        opened={deleteContact !== null}
        onClose={() => setDeleteContact(null)}
        title="Ansprechpartner löschen?"
        zIndex={900}
        size="sm"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            „{deleteContact?.name}" verschwindet aus der Liste. Der ServiceHub legt den Kontakt auch dann nicht wieder
            an, wenn Weclapp ihn weiter meldet oder ein Ticket dieses Absenders dem Kunden zugeordnet wird.
          </Text>
          {deleteContact?.portalEnabled && (
            <Text size="sm">Der Portal-Zugang wird entzogen, laufende Sitzungen enden sofort.</Text>
          )}
          {deleteContact?.isActive && (
            <Text size="xs" c="dimmed">
              Nur gerade nicht zuständig? Dann lieber deaktivieren.
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setDeleteContact(null)}>
              Abbrechen
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleteContact && deleteMutation.mutate(deleteContact)}
            >
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
